/* Handouts: what goes on a sheet of paper, and where it comes from.

   The old portal built its handouts by reaching into the page data and picking
   blocks by kind and position. It worked, and its own documentation warned that
   reordering a page could silently change what the handout grabbed. That is the
   part worth not rebuilding.

   Here a handout is a document like any other, and what it takes from elsewhere
   is written down: a document, a kind, the start of a title. The selection is
   data, so `scripts/check-portal.mjs` resolves every one of them and fails the
   build when one matches nothing. The fragility does not go away — a handout
   that quotes a page is coupled to it — but it stops being silent.

   Nothing is duplicated. A sheet quotes the material; it does not restate it,
   because two copies of a prompt are two prompts that drift.

   OWNED BY ITEM 9 OF docs/plan/00-prestavba-2026-08.md. */

import type { Block, Doc, Localized } from "./types";

export interface Pick {
  /* Which document to take from, and optionally which of its parts. */
  from?: string;
  part?: string;
  /* Which block: its kind, and the start of its title when the kind alone is
     ambiguous — #/aplikace has three prompt blocks. */
  k?: string;
  title?: string;
  /* Or: the first block carrying this field at all. Two of the five planning
     steps keep their prompt inside a `steps` block and one inside an
     `exercise`, so matching on the kind would miss them — what the sheet wants
     is the prompt, wherever it lives. */
  has?: string;

  /* Render only that field, as a prompt of its own. A sheet quotes the prompt,
     not the explainer it sits inside. */
  only?: "prompt" | "out";

  /* A block written into the handout itself, for a heading or a rule that
     belongs to the sheet and to nothing else. */
  block?: Block;

  /* Generated content, which is not a block anywhere and cannot be quoted.
     `examples` is the catalogue of lessons, one group per strand. `lesson` is
     one whole lesson — its goal, what is in its kit, and every material of it
     that goes on paper — and it takes the lesson from `from`. */
  generate?: "examples" | "lesson";
  strand?: string;

  /* --- trimming, because a sheet of A4 is a hard limit --- */
  /* Drop fields the page needs and the paper does not. */
  drop?: ("title" | "lead" | "note" | "out" | "prompt")[];
  /* A step description has to earn its line: keep only these, by index. */
  keepWhy?: number[];
  titlesOnly?: boolean;
  /* Rows and columns of a table, by index. `fromRow` takes everything from
     that index on, which is what a sheet usually wants: the table is ordered
     for the cut, and naming the rows one by one breaks the day the table
     grows. */
  rows?: number[];
  fromRow?: number;
  columns?: number[];
  /* Turn a table's remaining first column into a note, the way the old
     application sheet did with the pre-flight questions. */
  asNote?: Localized;
}

export interface Sheet {
  cls?: string;
  eyebrow?: Localized;
  title?: Localized;
  lead?: Localized;
  pick: Pick[];
}

export interface Handout {
  id: string;
  type: "handout";
  title: Localized;
  file?: string;
  sheets: Sheet[];
}

/* Every document a handout quotes, so the route knows what to load. */
export function sources(handout: Handout): string[] {
  const ids = new Set<string>();
  for (const sheet of handout.sheets) {
    for (const pick of sheet.pick) {
      if (pick.from) ids.add(pick.from);
    }
  }
  return [...ids];
}

/* A block is a discriminated union, so reading a field by name needs one cast
   in one place rather than one at every call site. */
function field(block: Block, name: string): unknown {
  return (block as unknown as Record<string, unknown>)[name];
}

function titleText(value: Localized | undefined): string {
  if (value == null) return "";
  return typeof value === "string" ? value : value.cs;
}

/* Blocks of one document, or of one of its parts. */
function pool(doc: Doc, part?: string): Block[] {
  if (!part) return [...doc.blocks, ...(doc.parts ?? []).flatMap((p) => p.blocks)];
  const found = (doc.parts ?? []).find((p) => p.id === part);
  return found ? found.blocks : [];
}

export function find(doc: Doc, pick: Pick): Block | null {
  const blocks = pool(doc, pick.part);
  for (const block of blocks) {
    if (pick.k && block.k !== pick.k) continue;
    if (pick.has && !field(block, pick.has)) continue;
    if (pick.title && !titleText(block.title).startsWith(pick.title)) continue;
    return block;
  }
  return null;
}

/* Apply the trimming. Returns a new block; the source is never touched, because
   the same block is also on the page it came from. */
export function trim(block: Block, pick: Pick): Block {
  let out: Record<string, unknown> = { ...block };

  if (pick.only) {
    out = { k: "prompt", [pick.only]: field(block, pick.only), note: block.note };
  }

  for (const field of pick.drop ?? []) delete out[field];

  if (out.k === "steps" && Array.isArray(out.steps)) {
    const steps = out.steps as { t: Localized; d?: Localized }[];
    if (pick.titlesOnly) {
      out.steps = steps.map((s) => ({ t: s.t }));
    } else if (pick.keepWhy) {
      // On paper a description has to earn its line. These are the steps that
      // hide a reason; the rest are whole instructions on their own.
      out.steps = steps.map((s, i) => (pick.keepWhy!.includes(i) ? s : { t: s.t }));
    }
  }

  if (out.k === "table") {
    let head = out.head as Localized[] | undefined;
    let rows = out.rows as Localized[][] | undefined;
    /* Indices that name nothing are dropped rather than left as holes: a
       selector pointing past the end of a table is caught by the gate, and a
       sheet must not print an empty cell if one ever slips through. */
    const take = <T,>(from: T[], at: number[]): T[] =>
      at.map((i) => from[i]).filter((v): v is T => v !== undefined);

    if (pick.fromRow != null && rows) rows = rows.slice(pick.fromRow);
    if (pick.rows && rows) rows = take(rows, pick.rows);
    if (pick.columns && rows) {
      if (head) head = take(head, pick.columns);
      rows = rows.map((r) => take(r, pick.columns!));
    }
    out.head = head;
    out.rows = rows;

    if (pick.asNote && rows) {
      /* The remaining first column becomes a note, not a list: the application
         sheet is already at the edge of an A4 and a note is set small. A block
         with neither prompt nor output renders as its notes and nothing else. */
      out = { k: "prompt", noteTitle: pick.asNote, note: rows.map((r) => r[0]!) };
    }
  }

  return out as unknown as Block;
}


/* ---------- resolving a handout into printable sheets ----------

   Everything is turned into blocks before anything is drawn, because a
   generated group may become several sheets — one printed page each — and a
   renderer that discovers that halfway through cannot start a new page. */

export interface RenderSheet {
  cls?: string;
  eyebrow?: Localized;
  title?: Localized;
  lead?: Localized;
  blocks: Block[];
  /* Selectors that matched nothing. The gate fails the build on these; the
     page shows them so a person editing content sees the hole immediately. */
  missing: string[];
}

/* What a generator returns. A sheet of its own, so a generated group can
   carry the same head as an authored one: a lesson's material wants the kind
   above the title and the claim under it, and none of the three is a block. */
export interface GeneratedSheet {
  cls?: string;
  eyebrow?: Localized;
  title: Localized;
  lead?: Localized;
  blocks: Block[];
}

export interface Sources {
  docs: Record<string, Doc>;
  generate: (pick: Pick) => GeneratedSheet[];
}

export function build(handout: Handout, sources: Sources): RenderSheet[] {
  const out: RenderSheet[] = [];

  for (const sheet of handout.sheets) {
    const own: RenderSheet = {
      cls: sheet.cls, eyebrow: sheet.eyebrow, title: sheet.title, lead: sheet.lead,
      blocks: [], missing: [],
    };
    const generated: RenderSheet[] = [];

    for (const pick of sheet.pick) {
      if (pick.generate) {
        /* A generated group becomes sheets, not blocks: one printed page each,
           balanced, rather than one sheet that breaks wherever the page ends. */
        const made = sources.generate(pick);
        if (!made.length) {
          own.missing.push(`generate:${pick.generate}:${pick.strand ?? pick.from ?? ""}`);
        }
        for (const one of made) {
          generated.push({
            cls: one.cls ?? sheet.cls, eyebrow: one.eyebrow,
            title: one.title, lead: one.lead, blocks: one.blocks, missing: [],
          });
        }
        continue;
      }

      if (pick.block) { own.blocks.push(pick.block); continue; }

      const doc = pick.from ? sources.docs[pick.from] : undefined;
      const found = doc ? find(doc, pick) : null;
      if (found) own.blocks.push(trim(found, pick));
      else own.missing.push(`${pick.from ?? "?"}/${pick.k ?? pick.has ?? "?"}`
        + (pick.title ? `/${pick.title}` : ""));
    }

    /* The authored sheet comes first, in the order it was written; a sheet that
       exists only to generate others contributes nothing of its own. */
    if (own.blocks.length || own.missing.length) out.push(own);
    out.push(...generated);
  }

  return out;
}
